import { useMemo } from "react";
import { Account, useListAndSearchAllAccounts } from "@rentablo/api/apiv1";

export interface RequiredRow {
  account: Account;
  isReference: boolean;
}

const isDepot = (a: Account) => a.type === "01_depot";

const isReferenceAccount = (a: Account) =>
  a.type === "02_cash" && Boolean(a.depotIds?.length);

const classify = (accounts: Account[]) => {
  const { depots, referenceMap, optionalAccounts } = accounts.reduce(
    (acc, a) => {
      if (isDepot(a)) {
        acc.depots.push(a);
      } else if (isReferenceAccount(a)) {
        for (const id of a.depotIds!) {
          const refs = acc.referenceMap.get(id) ?? [];
          refs.push(a);
          acc.referenceMap.set(id, refs);
        }
      } else {
        acc.optionalAccounts.push(a);
      }
      return acc;
    },
    {
      depots: [] as Account[],
      referenceMap: new Map<number, Account[]>(),
      optionalAccounts: [] as Account[],
    }
  );

  const requiredRows: RequiredRow[] = depots.flatMap((depot) => [
    { account: depot, isReference: false },
    ...(referenceMap.get(depot.id) ?? []).map((ref) => ({
      account: ref,
      isReference: true,
    })),
  ]);

  return { optionalAccounts, requiredRows };
};

export const useClassifiedAccounts = (
  bankConnectionId: number | null,
  enabled = false
) => {
  const { data, isLoading, isFetched } = useListAndSearchAllAccounts(
    { bankConnectionId: bankConnectionId ? [bankConnectionId] : undefined },
    { enabled }
  );

  const { optionalAccounts, requiredRows } = useMemo(
    () => classify(data?.accounts ?? []),
    [data]
  );

  return { optionalAccounts, requiredRows, isLoading, isFetched };
};
